import { useCallback, useContext } from 'react';
import { ConfigFile, Question } from '@ch/common/types/data';
import { assetsPath, masterLang } from './const';
import { useLang } from './use-lang';
import { AppContext } from './app-context';
import { getJson } from './get-json';

import axios from 'axios';

export const useData = () => {
  const { setLoading, data, setData } = useContext(AppContext);
  const { lang } = useLang();

  const fetchData = useCallback(async () => {
    const files = {} as Record<string, unknown>;

    const masterConfig = (await axios.get(`${assetsPath}/i18n/${masterLang}.json`))?.data;
    const promises = Object.values(masterConfig.header.languages.dropdown)
      // eslint-disable-next-line @typescript-eslint/camelcase
      .filter(({ short_name }) => short_name !== masterLang)
      // eslint-disable-next-line @typescript-eslint/camelcase
      .map(({ short_name }) =>
        // eslint-disable-next-line @typescript-eslint/camelcase
        getJson(`${assetsPath}/i18n/${short_name}.json`, short_name, files),
      );
    await Promise.all(promises);

    setData({ [masterLang]: masterConfig, ...files });

    setLoading(false);
  }, [setLoading, setData]);

  const setQuestion = (newQuestion: Question) => {
    const { questions, ...rest } = data[lang] as ConfigFile;

    data[lang] = {
      ...rest,
      questions: questions.map(el => {
        if (el.id === newQuestion.id && el.identifier === newQuestion.identifier) {
          return newQuestion;
        }
        return el;
      }),
    };
    setData(data);
  };

  const insertQuestions = (newQuestions: Question[], position: number) => {
    const { questions, ...rest } = data[lang] as ConfigFile;
    data[lang] = {
      ...rest,
      questions: [...questions.slice(0, position), ...newQuestions, ...questions.slice(position)],
    };
    setData(data);
  };

  const removeQuestions = (questions: Question[]) => {
    const toRemoveIds = questions.map(q => q.identifier);
    data[lang].questions = data[lang].questions.filter((q: Question) => !toRemoveIds.includes(q.identifier));
    setData(data);
  };

  const removeQuestionsByGroup = (groupName: string) => {
    data[lang].questions = data[lang].questions.filter((q: Question) => q.duplicatorGroup !== groupName);
    setData(data);
  };

  return {
    fetchData,
    data: data[lang] as ConfigFile,
    setQuestion,
    insertQuestions,
    removeQuestions,
    removeQuestionsByGroup,
  };
};
